import React, { useState } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import {
  calculateNumerology,
  calculateChaldeanNameNumber,
  calculatePythagoreanNameNumber,
  NUMBER_LORDS,
  NUMBER_RELATIONS,
  MOOLANK_DATA,
  NumerologyAnalysis
} from '../utils/numerologyEngine';
import { NameBrandComparisonEngine } from './NameBrandComparisonEngine';
import {
  Sparkles,
  Compass,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  Zap,
  Star,
  Award,
  BookOpen,
  HelpCircle,
  TrendingUp,
  Shield,
  Palette,
  Calendar,
  Layers,
  ArrowRight,
  Hash,
  Activity,
  HeartHandshake,
  Ban,
  MinusCircle
} from 'lucide-react';
import { getColorTaxonomy } from './AuspiciousEssentialsBar';

interface NumerologyViewProps {
  kundali?: CompleteKundali | null;
  lang: Language;
}

export const NumerologyView: React.FC<NumerologyViewProps> = ({ kundali, lang }) => {
  // Input states (initialized from current Kundali if available)
  const defaultDate = kundali?.birthDetails.date || '1995-08-15';
  const defaultName = kundali?.birthDetails.name || 'Aditya Sharma';
  const defaultGender = kundali?.birthDetails.gender || 'male';

  const [birthDate, setBirthDate] = useState<string>(defaultDate);
  const [personName, setPersonName] = useState<string>(defaultName);
  const [gender, setGender] = useState<'male' | 'female' | 'other'>(defaultGender);

  // Active Sub-Tab
  const [activeSection, setActiveSection] = useState<'overview' | 'loshu' | 'name-lab' | 'brand-compare' | 'compatibility' | 'forecast' | 'remedies'>('overview');

  // Name Lab Interactive State
  const [testName, setTestName] = useState<string>(defaultName);

  // Compatibility tester state
  const [partnerNum, setPartnerNum] = useState<number>(1);

  // Recalculate analysis
  const analysis: NumerologyAnalysis = React.useMemo(() => {
    return calculateNumerology(birthDate, personName, gender);
  }, [birthDate, personName, gender]);

  // Real-time name lab calculations
  const chaldeanTest = React.useMemo(() => calculateChaldeanNameNumber(testName), [testName]);
  const pythagoreanTest = React.useMemo(() => calculatePythagoreanNameNumber(testName), [testName]);

  // Check harmony between test name and Moolank/Bhagyank
  const isNameHarmonious = React.useMemo(() => {
    const rel = NUMBER_RELATIONS[analysis.moolank];
    return rel?.friends.includes(chaldeanTest.single);
  }, [analysis.moolank, chaldeanTest.single]);

  const isHi = lang === 'hi';

  const lagnaSign = kundali?.charts?.D1?.houses?.find((h) => h.houseNumber === 1)?.sign || kundali?.houses?.[0]?.sign || 'Aries';
  const colorTaxonomy = React.useMemo(() => {
    return getColorTaxonomy(lagnaSign, analysis.moolank, lang);
  }, [lagnaSign, analysis.moolank, lang]);

  return (
    <div className="space-y-6 pb-12">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-astro-primary via-[#1F5F5E] to-astro-primary text-astro-bg p-6 sm:p-8 rounded-2xl shadow-md border border-astro-primary/20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 text-xs font-bold uppercase tracking-wider border border-[#E2DED5]/30">
                {isHi ? 'वैदिक अंक शास्त्र' : 'Vedic & Chaldean Numerology'}
              </span>
              <span className="text-xs text-astro-bg/70">Anka Vidya Engine</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold font-serif">
              {isHi ? 'अंक ज्योतिष एवं लो-शू चक्र विश्लेषण' : 'Vedic Numerology & Loshu Grid Analysis'}
            </h2>
            <p className="text-sm text-astro-bg/80 mt-1 max-w-2xl">
              {isHi
                ? 'मूलांक, भाग्यांक, नामांक, लो-शू ग्रिड राजयोग एवं नामांक संशोधन का शास्त्रीय एवं पारंपरिक विश्लेषण।'
                : 'Classical Vedic & Chaldean analysis of Driver (Moolank), Destiny (Bhagyank), Name vibrations, and Loshu Grid.'}
            </p>
          </div>

          {/* Quick Custom Input Bar */}
          <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20 flex flex-wrap items-center gap-3">
            <div>
              <label className="block text-[11px] text-amber-200 font-semibold mb-1">
                {isHi ? 'जन्म तिथि' : 'Birth Date'}
              </label>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="bg-white text-astro-text text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-astro-border focus:ring-2 focus:ring-amber-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] text-amber-200 font-semibold mb-1">
                {isHi ? 'पूरा नाम' : 'Full Name'}
              </label>
              <input
                type="text"
                value={personName}
                onChange={(e) => {
                  setPersonName(e.target.value);
                  setTestName(e.target.value);
                }}
                className="bg-white text-astro-text text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-astro-border focus:ring-2 focus:ring-amber-400 outline-none w-36 sm:w-44"
              />
            </div>
            <div>
              <label className="block text-[11px] text-amber-200 font-semibold mb-1">
                {isHi ? 'लिंग' : 'Gender'}
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value as any)}
                className="bg-white text-astro-text text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-astro-border focus:ring-2 focus:ring-amber-400 outline-none"
              >
                <option value="male">{isHi ? 'पुरुष' : 'Male'}</option>
                <option value="female">{isHi ? 'महिला' : 'Female'}</option>
                <option value="other">{isHi ? 'अन्य' : 'Other'}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sub-Navigation Pills */}
        <div className="flex items-center gap-2 mt-6 pt-4 border-t border-white/10 overflow-x-auto pb-1 scrollbar-none">
          {[
            { id: 'overview', label: isHi ? 'मूल अंक विहंगम' : 'Core Numbers', icon: Hash },
            { id: 'loshu', label: isHi ? 'लो-शू चक्र (Loshu)' : 'Loshu 3x3 Grid', icon: Layers },
            { id: 'name-lab', label: isHi ? 'नामांक संशोधन लैब' : 'Name Tuning Lab', icon: Zap },
            { id: 'brand-compare', label: isHi ? 'ब्रांड व नाम तुलना' : 'Brand & Name Compare', icon: Award },
            { id: 'compatibility', label: isHi ? 'अंक अनुकूलता' : 'Compatibility Matrix', icon: HeartHandshake },
            { id: 'forecast', label: isHi ? 'वर्षफल अंक चक्र' : 'Personal Year Cycle', icon: Calendar },
            { id: 'remedies', label: isHi ? 'रत्न व उपाय' : 'Vedic Remedies', icon: Shield }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id as any)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-amber-400 text-astro-primary font-bold shadow-md'
                    : 'bg-white/10 hover:bg-white/20 text-astro-bg'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* SECTION 1: CORE NUMBERS OVERVIEW */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          {/* 4 Hero Number Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Moolank */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm relative overflow-hidden group hover:border-astro-primary transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider">
                  {isHi ? 'मूलांक (Driver / Root)' : 'Root / Driver Number'}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-astro-primary/10 text-astro-primary">
                  {analysis.moolankLord}
                </span>
              </div>
              <div className="flex items-baseline gap-3 my-3">
                <span className="text-4xl font-extrabold font-serif text-astro-primary">
                  {analysis.moolank}
                </span>
                <span className="text-xs text-astro-secondary-text">
                  {isHi ? `तिथि: ${analysis.birthDateFormatted.split('/')[0]}` : `From Day ${analysis.birthDateFormatted.split('/')[0]}`}
                </span>
              </div>
              <div className="flex items-center gap-1 mb-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-astro-primary/5 text-astro-primary font-medium">
                  {isHi ? 'वैदिक अंक शास्त्र' : 'Vedic Anka Shastra'}
                </span>
              </div>
              <p className="text-xs font-medium text-astro-text line-clamp-2">
                {isHi ? analysis.moolankTraits.titleHi : analysis.moolankTraits.title}
              </p>
              <div className="mt-3 pt-3 border-t border-astro-border text-[11px] text-astro-secondary-text flex items-center justify-between">
                <span>{isHi ? 'स्वामी:' : 'Ruling Planet:'}</span>
                <strong className="text-astro-primary">{isHi ? analysis.moolankLordHi : analysis.moolankLord}</strong>
              </div>
            </div>

            {/* Card 2: Bhagyank */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm relative overflow-hidden group hover:border-astro-primary transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider">
                  {isHi ? 'भाग्यांक (Destiny / Life Path)' : 'Destiny / Life Path'}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-astro-secondary-surface0/10 text-[#52635F]">
                  {analysis.bhagyankLord}
                </span>
              </div>
              <div className="flex items-baseline gap-3 my-3">
                <span className="text-4xl font-extrabold font-serif text-[#52635F]">
                  {analysis.bhagyank}
                </span>
                <span className="text-xs text-astro-secondary-text">
                  {isHi ? 'संपूर्ण जन्म तिथि योग' : 'Full Date Sum'}
                </span>
              </div>
              <div className="flex items-center gap-1 mb-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-astro-secondary-surface0/10 text-[#52635F] font-medium">
                  {isHi ? 'वैदिक अंक शास्त्र' : 'Vedic Anka Shastra'}
                </span>
              </div>
              <p className="text-xs font-medium text-astro-text line-clamp-2">
                {isHi ? analysis.bhagyankTraits.titleHi : analysis.bhagyankTraits.title}
              </p>
              <div className="mt-3 pt-3 border-t border-astro-border text-[11px] text-astro-secondary-text flex items-center justify-between">
                <span>{isHi ? 'स्वामी:' : 'Ruling Planet:'}</span>
                <strong className="text-astro-secondary">{isHi ? analysis.bhagyankLordHi : analysis.bhagyankLord}</strong>
              </div>
            </div>

            {/* Card 3: Namank (Chaldean & Pythagorean) */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm relative overflow-hidden group hover:border-astro-primary transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider">
                  {isHi ? 'नामांक (Name Number)' : 'Name Number (Chaldean)'}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-700">
                  {analysis.isNameValid ? 'Vibration' : 'Input Required'}
                </span>
              </div>
              <div className="flex items-baseline gap-3 my-3">
                <span className="text-4xl font-extrabold font-serif text-emerald-700">
                  {analysis.isNameValid ? analysis.namankChaldean : '—'}
                </span>
                <span className="text-xs text-astro-secondary-text">
                  Pythagorean: <strong className="text-astro-text">{analysis.isNameValid ? analysis.namankPythagorean : '—'}</strong>
                </span>
              </div>
              <div className="flex items-center gap-1 mb-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 font-medium">
                  {isHi ? 'चालडियन व पाइथागोरियन' : 'Chaldean & Pythagorean'}
                </span>
              </div>
              <p className="text-xs font-medium text-astro-text truncate">
                {analysis.isNameValid
                  ? (isHi ? `नाम: ${personName}` : `Name: ${personName}`)
                  : (isHi ? 'लैटिन (A-Z) लिपि आवश्यक है' : 'Latin script (A-Z) required')}
              </p>
              <div className="mt-3 pt-3 border-t border-astro-border text-[11px] text-astro-secondary-text flex items-center justify-between">
                <span>{isHi ? 'नामांक स्वामी:' : 'Name Lord:'}</span>
                <strong className="text-emerald-800">{analysis.namankLord}</strong>
              </div>
            </div>

            {/* Card 4: Kua Number */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm relative overflow-hidden group hover:border-astro-primary transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider">
                  {isHi ? 'कुआ अंक (Kua Number)' : 'Kua Directional Number'}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-500/10 text-indigo-700">
                  Feng Shui & Vastu
                </span>
              </div>
              <div className="flex items-baseline gap-3 my-3">
                <span className="text-4xl font-extrabold font-serif text-indigo-700">
                  {analysis.kuaNumber}
                </span>
                <span className="text-xs text-astro-secondary-text">
                  {isHi ? 'वास्तु व दिशा' : 'Orientation Index'}
                </span>
              </div>
              <div className="flex items-center gap-1 mb-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 font-medium">
                  {isHi ? 'अष्ट दिशा वास्तु / कुआ' : 'Eight Mansions / Ba Zhai'}
                </span>
              </div>
              <p className="text-xs font-medium text-astro-text">
                {isHi ? 'शुभ दिशा: उत्तर-पूर्व / पूर्व' : 'Optimal Direction: East & North'}
              </p>
              <div className="mt-3 pt-3 border-t border-astro-border text-[11px] text-astro-secondary-text flex items-center justify-between">
                <span>{isHi ? 'तत्व:' : 'Element:'}</span>
                <strong className="text-indigo-800">{isHi ? 'पृथ्वी / जल' : 'Earth / Water'}</strong>
              </div>
            </div>
          </div>

          {/* Invalid Date Alert if present */}
          {!analysis.isDateValid && (
            <div className="bg-amber-50 border border-amber-300 p-4 rounded-xl flex items-center gap-3 text-amber-900">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider">
                  {isHi ? 'अमान्य जन्म तिथि' : 'Invalid Birth Date'}
                </h4>
                <p className="text-xs mt-0.5">
                  {isHi
                    ? 'दर्ज की गई जन्म तिथि कैलेंडर के अनुसार अमान्य है (जैसे फ़रवरी 31)। गणना मानक आधार मान पर प्रदर्शित हो रही है।'
                    : 'The birth date entered is impossible or unparseable according to the standard calendar (e.g. Feb 31). Baseline values are displayed.'}
                </p>
              </div>
            </div>
          )}

          {/* Master / Karmic Number alert if present */}
          {analysis.isMasterNumber && (
            <div className="bg-astro-secondary-surface border border-[#E2DED5] p-4 rounded-xl flex items-center gap-3 text-astro-secondary">
              <Sparkles className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider">
                  {isHi ? `मास्टर अंक ${analysis.isMasterNumber} सक्रिय` : `Master Number ${analysis.isMasterNumber} Activated`}
                </h4>
                <p className="text-xs mt-0.5">
                  {isHi
                    ? `आपके जन्म चक्र में मास्टर नंबर ${analysis.isMasterNumber} का प्रभाव है। यह उच्च आत्मिक क्षमता, दूरगामी प्रभाव और असाधारण नेतृत्व का द्योतक है।`
                    : `You carry Master Number ${analysis.isMasterNumber} vibration, granting elevated spiritual intuition, transformative vision, and vast humanitarian influence.`}
                </p>
              </div>
            </div>
          )}

          {analysis.karmicDebtNumber && (
            <div className="bg-rose-50 border border-rose-300 p-4 rounded-xl flex items-center gap-3 text-rose-900">
              <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider">
                  {isHi ? `कार्मिक ऋण अंक ${analysis.karmicDebtNumber}` : `Karmic Debt Number ${analysis.karmicDebtNumber}`}
                </h4>
                <p className="text-xs mt-0.5">
                  {isHi
                    ? `अंक ${analysis.karmicDebtNumber} पूर्व जन्म के कर्मों के संशोधन का संकेत देता है। नियमित ध्यान, अनुशासन और सत्यवादिता से इस ऊर्जा को वरदान में बदला जा सकता है।`
                    : `Birth day on ${analysis.karmicDebtNumber} highlights a specific karmic lesson regarding discipline, humility, and patience. Following Vedic remedies dissolves karmic blockages.`}
                </p>
              </div>
            </div>
          )}

          {/* In-depth Driver & Destiny Interpretation */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Moolank Profile */}
            <div className="bg-astro-bg p-6 rounded-2xl border border-astro-border shadow-sm space-y-4">
              <div className="flex items-center gap-2 border-b border-astro-border pb-3">
                <div className="w-8 h-8 rounded-lg bg-astro-primary text-white flex items-center justify-center font-bold">
                  {analysis.moolank}
                </div>
                <div>
                  <h3 className="text-base font-bold text-astro-text font-serif">
                    {isHi ? 'मूलांक व्यक्तित्व एवं स्वभाव' : 'Moolank (Driver Number) Profile'}
                  </h3>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi ? `शासक: ${analysis.moolankLordHi}` : `Ruled by ${analysis.moolankLord}`}
                  </p>
                </div>
              </div>

              <p className="text-xs text-astro-text leading-relaxed">
                {isHi ? analysis.moolankTraits.descriptionHi : analysis.moolankTraits.description}
              </p>

              <div>
                <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider mb-2">
                  {isHi ? 'प्रमुख शक्तियां (Strengths)' : 'Key Strengths'}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {(isHi ? analysis.moolankTraits.strengthsHi : analysis.moolankTraits.strengths).map((s, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-astro-card p-2 rounded-lg text-xs font-medium text-astro-text">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <span>{s}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-bold text-rose-700 uppercase tracking-wider mb-2">
                  {isHi ? 'सावधानियां एवं चुनौतियां (Challenges)' : 'Challenges to Watch'}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {(isHi ? analysis.moolankTraits.challengesHi : analysis.moolankTraits.challenges).map((c, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-rose-50 p-2 rounded-lg text-xs font-medium text-rose-800">
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                      <span>{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bhagyank Profile */}
            <div className="bg-astro-bg p-6 rounded-2xl border border-astro-border shadow-sm space-y-4">
              <div className="flex items-center gap-2 border-b border-astro-border pb-3">
                <div className="w-8 h-8 rounded-lg bg-amber-700 text-white flex items-center justify-center font-bold">
                  {analysis.bhagyank}
                </div>
                <div>
                  <h3 className="text-base font-bold text-astro-text font-serif">
                    {isHi ? 'भाग्यांक एवं जीवन पथ (Life Path Mission)' : 'Bhagyank (Destiny & Life Path)'}
                  </h3>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi ? `शासक: ${analysis.bhagyankLordHi}` : `Ruled by ${analysis.bhagyankLord}`}
                  </p>
                </div>
              </div>

              <p className="text-xs text-astro-text leading-relaxed">
                {isHi ? analysis.bhagyankTraits.descriptionHi : analysis.bhagyankTraits.description}
              </p>

              <div className="bg-astro-card p-4 rounded-xl border border-astro-border">
                <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                  {isHi ? 'कार्मिक जीवन मिशन' : 'Karmic Life Mission'}
                </h4>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi ? analysis.bhagyankTraits.karmicMissionHi : analysis.bhagyankTraits.karmicMission}
                </p>
              </div>

              {/* Quick Lucky Attributes Grid */}
              <div className="grid grid-cols-3 gap-2 pt-2 text-center text-xs">
                <div className="bg-astro-bg p-2.5 rounded-xl border border-astro-border">
                  <span className="text-[10px] text-astro-secondary-text block">{isHi ? 'शुभ अंक' : 'Lucky Numbers'}</span>
                  <strong className="text-astro-primary font-bold text-sm">
                    {analysis.luckyNumbers.join(', ')}
                  </strong>
                </div>
                <div className="bg-astro-bg p-2.5 rounded-xl border border-astro-border">
                  <span className="text-[10px] text-astro-secondary-text block">{isHi ? 'शुभ दिन' : 'Lucky Days'}</span>
                  <strong className="text-astro-secondary font-bold text-xs">
                    {(isHi ? analysis.luckyDaysHi : analysis.luckyDays).join(', ')}
                  </strong>
                </div>
                <div className="bg-astro-bg p-2.5 rounded-xl border border-astro-border">
                  <span className="text-[10px] text-astro-secondary-text block">{isHi ? 'शुभ रंग' : 'Lucky Colors'}</span>
                  <strong className="text-emerald-800 font-bold text-xs truncate block">
                    {(isHi ? analysis.luckyColorsHi : analysis.luckyColors).slice(0, 2).join(', ')}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 2: LOSHU 3X3 GRID & 8 PLANES */}
      {activeSection === 'loshu' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: 3x3 Loshu Grid Visualizer */}
            <div className="lg:col-span-5 bg-astro-bg p-6 rounded-2xl border border-astro-border shadow-sm flex flex-col items-center justify-between">
              <div className="w-full text-center mb-4">
                <h3 className="text-lg font-bold text-astro-text font-serif">
                  {isHi ? 'लो-शू चक्र (Loshu 3x3 Magic Grid)' : 'Loshu 3x3 Grid'}
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi ? `जन्म तिथि (${analysis.birthDateFormatted}) के आधार पर निर्मित` : `Generated from Birthdate ${analysis.birthDateFormatted}`}
                </p>
              </div>

              {/* 3x3 Grid Box */}
              <div className="grid grid-cols-3 gap-3 w-full max-w-[320px] aspect-square bg-astro-card p-4 rounded-2xl border-2 border-astro-primary/30 shadow-inner">
                {/* Row 1: 4, 9, 2 */}
                {[
                  { target: 4, label: '4 (Rahu)' },
                  { target: 9, label: '9 (Mars)' },
                  { target: 2, label: '2 (Moon)' },
                  { target: 3, label: '3 (Jupiter)' },
                  { target: 5, label: '5 (Mercury)' },
                  { target: 7, label: '7 (Ketu)' },
                  { target: 8, label: '8 (Saturn)' },
                  { target: 1, label: '1 (Sun)' },
                  { target: 6, label: '6 (Venus)' }
                ].map((cell, idx) => {
                  const count = analysis.loshuGrid.countMap[cell.target] || 0;
                  const isPresent = count > 0;
                  return (
                    <div
                      key={idx}
                      className={`flex flex-col items-center justify-center rounded-xl border-2 transition-all p-2 relative ${
                        isPresent
                          ? 'bg-astro-primary text-astro-bg border-astro-primary shadow-md scale-[1.02]'
                          : 'bg-white/60 text-astro-secondary-text/40 border-dashed border-astro-border'
                      }`}
                    >
                      <span className={`text-2xl font-extrabold font-serif ${isPresent ? 'text-amber-300' : 'text-astro-secondary-text/30'}`}>
                        {cell.target}
                      </span>
                      <span className="text-[10px] font-semibold mt-0.5">
                        {isPresent ? (count > 1 ? `×${count}` : 'Present') : 'Absent'}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Missing Numbers Pill Strip */}
              <div className="w-full mt-6 pt-4 border-t border-astro-border">
                <span className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider block mb-2">
                  {isHi ? 'अनुपस्थित अंक (Missing Numbers):' : 'Missing Numbers & Remedies:'}
                </span>
                {analysis.loshuGrid.missingNumbers.length === 0 ? (
                  <span className="text-xs text-emerald-700 font-bold">
                    {isHi ? 'अद्भुत! आपके चक्र में कोई अंक रिक्त नहीं है।' : 'Remarkable! All 9 digits are present in your chart.'}
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {analysis.loshuGrid.missingNumbers.map((m) => (
                      <span
                        key={m}
                        className="px-2.5 py-1 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex items-center gap-1"
                      >
                        <Hash className="w-3 h-3 text-rose-500" />
                        {m} ({NUMBER_LORDS[m]?.en?.split(' ')[0]})
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: 8 Planes Analysis */}
            <div className="lg:col-span-7 bg-astro-bg p-6 rounded-2xl border border-astro-border shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div>
                  <h3 className="text-lg font-bold text-astro-text font-serif">
                    {isHi ? 'लो-शू चक्र के 8 तल (8 Planes Analysis)' : '8 Planes of Loshu Grid'}
                  </h3>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi ? 'मानसिक, भावनात्मक, व्यावहारिक, विचार, इच्छाशक्ति, कर्म व राजयोग' : 'Mental, Emotional, Practical, Thought, Will, Action, and Raj Yogas'}
                  </p>
                </div>
              </div>

              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                {analysis.loshuGrid.planes.map((plane, idx) => {
                  const isFull = plane.percentage === 100;
                  const isPartial = plane.percentage > 0 && plane.percentage < 100;
                  return (
                    <div
                      key={idx}
                      className={`p-4 rounded-xl border transition-all ${
                        isFull
                          ? 'bg-astro-secondary-surface/70 border-[#E2DED5] shadow-sm'
                          : isPartial
                          ? 'bg-astro-card border-astro-border'
                          : 'bg-white/60 border-astro-border/60 opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs text-astro-text">
                            {isHi ? plane.nameHi : plane.name}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-white text-astro-primary border border-astro-border">
                            [{plane.numbers.join('-')}]
                          </span>
                        </div>
                        <span
                          className={`text-xs font-extrabold ${
                            isFull ? 'text-[#52635F]' : isPartial ? 'text-astro-primary' : 'text-astro-muted'
                          }`}
                        >
                          {plane.percentage}% Complete
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full h-1.5 bg-astro-border rounded-full overflow-hidden mb-2">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isFull ? 'bg-astro-secondary-surface0' : 'bg-astro-primary'
                          }`}
                          style={{ width: `${plane.percentage}%` }}
                        />
                      </div>

                      <p className="text-xs text-astro-text leading-relaxed">
                        {isHi ? plane.significanceHi : plane.significance}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Repeated Numbers Meaning */}
          {analysis.loshuGrid.repeatedNumbers.length > 0 && (
            <div className="bg-astro-bg p-6 rounded-2xl border border-astro-border shadow-sm">
              <h3 className="text-base font-bold text-astro-text font-serif mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-astro-primary" />
                {isHi ? 'पुनरावृत्त अंक प्रभाव (Repeated Numbers Impact)' : 'Impact of Repeated Numbers'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {analysis.loshuGrid.repeatedNumbers.map((rep, idx) => (
                  <div key={idx} className="bg-astro-card p-3 rounded-xl border border-astro-border">
                    <div className="flex items-center justify-between mb-1">
                      <strong className="text-sm text-astro-primary">
                        {isHi ? `अंक ${rep.num}` : `Number ${rep.num}`}
                      </strong>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-astro-primary text-white">
                        {rep.count} Times
                      </span>
                    </div>
                    <p className="text-xs text-astro-text mt-1">
                      {isHi ? rep.meaningHi : rep.meaning}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SECTION 3: NAME TUNING & CORRECTION LAB */}
      {activeSection === 'name-lab' && (
        <div className="space-y-6">
          <div className="bg-astro-bg p-6 sm:p-8 rounded-2xl border border-astro-border shadow-sm space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 text-xs font-bold uppercase tracking-wider border border-emerald-500/20">
                  {isHi ? 'नामांक संतुलन प्रयोगशाला' : 'Interactive Name Tuning Lab'}
                </span>
              </div>
              <h3 className="text-xl font-bold font-serif text-astro-text">
                {isHi ? 'नाम कम्पन एवं वर्तनी सुधार परीक्षक' : 'Name Vibrational & Spelling Correction Engine'}
              </h3>
              <p className="text-xs text-astro-secondary-text max-w-3xl mt-1">
                {isHi
                  ? 'अपने नाम की किसी भी वर्तनी (Spelling) को टाइप करके देखें कि वह आपके मूलांक व भाग्यांक से कितनी सामंजस्यपूर्ण है।'
                  : 'Type any alternative spelling of your name or brand to verify harmony with your birth Driver (Moolank) and Destiny (Bhagyank) numbers in real time.'}
              </p>
            </div>

            {/* Interactive Name Input */}
            <div className="bg-astro-card p-5 rounded-2xl border border-astro-border">
              <label className="block text-xs font-bold text-astro-primary uppercase tracking-wider mb-2">
                {isHi ? 'परीक्षण हेतु नाम दर्ज करें:' : 'Enter Name to Analyze (English Letters):'}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  placeholder="e.g. ADITYA SHARMA"
                  className="flex-1 bg-white text-astro-text text-base font-bold tracking-wide px-4 py-3 rounded-xl border border-astro-border focus:ring-2 focus:ring-astro-primary outline-none uppercase"
                />
                <button
                  onClick={() => setTestName(personName)}
                  className="px-4 py-3 bg-astro-primary text-white text-xs font-bold rounded-xl hover:bg-[#1F5F5E] transition-all flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {isHi ? 'रीसेट' : 'Reset'}
                </button>
              </div>
            </div>

            {/* Real-time Letter Breakdown & Vibration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Chaldean Method */}
              <div className="bg-white p-5 rounded-xl border border-astro-border shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-astro-border pb-2">
                  <span className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                    {isHi ? 'काल्डियन पद्धति (Chaldean Method)' : 'Chaldean Sound System (Ancient Vedic)'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-astro-primary text-white">
                    Primary
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs text-astro-secondary-text">{isHi ? 'कुल योग:' : 'Total Compound Sum:'}</span>
                    <strong className="text-lg font-bold text-astro-text block">{chaldeanTest.total}</strong>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-astro-secondary-text">{isHi ? 'नामांक (Single Digit):' : 'Name Vibration:'}</span>
                    <strong className="text-3xl font-extrabold font-serif text-astro-primary block">
                      {chaldeanTest.single}
                    </strong>
                  </div>
                </div>

                {/* Individual Letter Pills */}
                <div>
                  <span className="text-[11px] text-astro-secondary-text block mb-1.5">{isHi ? 'अक्षर मान विस्तार:' : 'Letter Value Mapping:'}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {chaldeanTest.breakdown.map((item, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-astro-card rounded-lg text-xs font-mono font-bold text-astro-primary border border-astro-border"
                      >
                        {item.char} = {item.val}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Pythagorean Method */}
              <div className="bg-white p-5 rounded-xl border border-astro-border shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-astro-border pb-2">
                  <span className="text-xs font-bold text-[#52635F] uppercase tracking-wider">
                    {isHi ? 'पाइथागोरियन पद्धति (Pythagorean)' : 'Pythagorean System (Western)'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-astro-secondary-surface0/10 text-[#52635F]">
                    Secondary
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs text-astro-secondary-text">{isHi ? 'कुल योग:' : 'Total Compound Sum:'}</span>
                    <strong className="text-lg font-bold text-astro-text block">{pythagoreanTest.total}</strong>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-astro-secondary-text">{isHi ? 'नामांक (Single Digit):' : 'Name Vibration:'}</span>
                    <strong className="text-3xl font-extrabold font-serif text-[#52635F] block">
                      {pythagoreanTest.single}
                    </strong>
                  </div>
                </div>

                <p className="text-xs text-astro-secondary-text leading-relaxed pt-2">
                  {isHi
                    ? 'पाइथागोरियन पद्धति वर्णमाला के क्रम (1-9) पर आधारित है, जबकि काल्डियन पद्धति ध्वनि की तरंगों व वैदिक ग्रहों पर आधारित है।'
                    : 'Pythagorean assigns numbers sequentially (1-9), whereas Chaldean assigns values strictly based on vibrational sound frequencies.'}
                </p>
              </div>
            </div>

            {/* Harmony Check Verdict */}
            <div
              className={`p-5 rounded-2xl border flex items-start gap-4 ${
                isNameHarmonious
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                  : 'bg-astro-secondary-surface border-[#E2DED5] text-astro-secondary'
              }`}
            >
              {isNameHarmonious ? (
                <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              )}
              <div className="space-y-1">
                <h4 className="text-sm font-bold">
                  {isNameHarmonious
                    ? isHi
                      ? `शुभ सामंजस्य: नामांक ${chaldeanTest.single} आपके मूलांक ${analysis.moolank} का परम मित्र है!`
                      : `Auspicious Harmony: Name Number ${chaldeanTest.single} is a natural Friend to your Driver ${analysis.moolank}!`
                    : isHi
                    ? `तटस्थ अथवा सुधार योग्य: नामांक ${chaldeanTest.single} एवं मूलांक ${analysis.moolank} में अतिरिक्त सामंजस्य बनाया जा सकता है।`
                    : `Adjustable: Name Number ${chaldeanTest.single} could be tuned further to match Driver ${analysis.moolank} more harmoniously.`}
                </h4>
                <p className="text-xs leading-relaxed opacity-90">
                  {isHi
                    ? `आपके मूलांक ${analysis.moolank} के लिए सर्वोत्तम नामांक 1, 3, 5 अथवा 6 माने जाते हैं। वर्तनी में हल्का सा परिवर्तन करके इसे और अधिक फलदायी बनाया जा सकता है।`
                    : `Ideal name totals for your root number ${analysis.moolank} are 1, 3, 5, or 6. Adding or removing letters to target these frequencies accelerates prosperity.`}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 4: COMPATIBILITY MATRIX */}
      {activeSection === 'compatibility' && (
        <div className="space-y-6">
          <div className="bg-astro-bg p-6 sm:p-8 rounded-2xl border border-astro-border shadow-sm space-y-6">
            <div>
              <h3 className="text-xl font-bold font-serif text-astro-text">
                {isHi ? 'अंक अनुकूलता एवं संबंध मिलान' : 'Numerological Compatibility Matrix'}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'अपने मूलांक के साथ अन्य सभी अंकों (1 से 9) के मित्रता, विवाह एवं व्यावसायिक संबंधों का विश्लेषण।'
                  : 'Evaluate partnership harmony, marriage alignment, and business synergy for your Driver number across all 9 digits.'}
              </p>
            </div>

            {/* Interactive Partner Number Picker */}
            <div className="flex flex-wrap items-center gap-2 bg-astro-card p-3 rounded-2xl border border-astro-border">
              <span className="text-xs font-bold text-astro-primary mr-2">
                {isHi ? 'साझेदार / जीवनसाथी का अंक चुनें:' : 'Select Partner / Colleague Number:'}
              </span>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  onClick={() => setPartnerNum(num)}
                  className={`w-9 h-9 rounded-xl text-sm font-bold transition-all ${
                    partnerNum === num
                      ? 'bg-astro-primary text-white shadow-md scale-105'
                      : 'bg-white text-astro-text hover:bg-astro-primary/10 border border-astro-border'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>

            {/* Selected Match Card */}
            {(() => {
              const rel = NUMBER_RELATIONS[analysis.moolank];
              const isFriend = rel.friends.includes(partnerNum);
              const isNeutral = rel.neutral.includes(partnerNum);
              const isEnemy = rel.enemies.includes(partnerNum);
              const pLord = NUMBER_LORDS[partnerNum];

              return (
                <div className="p-6 rounded-2xl border bg-white shadow-sm space-y-4">
                  <div className="flex items-center justify-between border-b border-astro-border pb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5 font-serif text-xl font-bold">
                        <span className="text-astro-primary">{analysis.moolank}</span>
                        <span className="text-astro-secondary-text">&</span>
                        <span className="text-[#52635F]">{partnerNum}</span>
                      </div>
                      <span className="text-xs text-astro-secondary-text">
                        ({analysis.moolankLord} + {pLord?.en})
                      </span>
                    </div>

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        isFriend
                          ? 'bg-emerald-100 text-emerald-800'
                          : isNeutral
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-rose-100 text-rose-800'
                      }`}
                    >
                      {isFriend
                        ? isHi ? 'अति शुभ / मित्र (Friendly)' : 'Highly Auspicious (Friend)'
                        : isNeutral
                        ? isHi ? 'सम / सामान्य (Neutral)' : 'Balanced (Neutral)'
                        : isHi ? 'सावधानी / शत्रु (Challenging)' : 'Challenging (Enemy)'}
                    </span>
                  </div>

                  <p className="text-xs text-astro-text leading-relaxed">
                    {isFriend
                      ? isHi
                        ? `अंक ${analysis.moolank} और ${partnerNum} का संयोग अत्यधिक फलदायी है। दोनों के विचार, कार्यशैली और भावनात्मक तरंगें एक-दूसरे को शक्ति प्रदान करती हैं। विवाह व व्यापार दोनों के लिए श्रेष्ठ।`
                        : `Vibration ${analysis.moolank} and ${partnerNum} create organic synergy. Both partners naturally elevate each other's vision, bringing mutual loyalty in marriage and compounding wealth in business.`
                      : isNeutral
                      ? isHi
                        ? `अंक ${analysis.moolank} और ${partnerNum} का संबंध संतुलित रहता है। स्पष्ट संवाद और एक-दूसरे के कार्यक्षेत्र का सम्मान करने से अच्छा तालमेल बना रहता है।`
                        : `Vibration ${analysis.moolank} and ${partnerNum} share a pragmatic equilibrium. Respecting personal boundaries ensures long-term constructive cooperation.`
                      : isHi
                      ? `अंक ${analysis.moolank} और ${partnerNum} के स्वभाव में वैचारिक मतभेद संभव हैं। संबंधों में मधुरता बनाए रखने हेतु धैर्य, समझदारी और वैदिक उपायों का पालन आवश्यक है।`
                      : `Vibration ${analysis.moolank} and ${partnerNum} possess differing core tempos. Mutual patience and conscious non-interference are recommended to avoid friction.`}
                  </p>
                </div>
              );
            })()}

            {/* Complete Relations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider block mb-2">
                  {isHi ? 'मित्र अंक (Friendly Numbers):' : 'Friendly Numbers (Mitra):'}
                </span>
                <div className="flex flex-wrap gap-2">
                  {NUMBER_RELATIONS[analysis.moolank].friends.map((n) => (
                    <span key={n} className="w-8 h-8 rounded-lg bg-emerald-600 text-white font-bold flex items-center justify-center text-sm shadow-sm">
                      {n}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                <span className="text-xs font-bold text-blue-800 uppercase tracking-wider block mb-2">
                  {isHi ? 'सम अंक (Neutral Numbers):' : 'Neutral Numbers (Sama):'}
                </span>
                <div className="flex flex-wrap gap-2">
                  {NUMBER_RELATIONS[analysis.moolank].neutral.map((n) => (
                    <span key={n} className="w-8 h-8 rounded-lg bg-blue-600 text-white font-bold flex items-center justify-center text-sm shadow-sm">
                      {n}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-rose-50 p-4 rounded-xl border border-rose-200">
                <span className="text-xs font-bold text-rose-800 uppercase tracking-wider block mb-2">
                  {isHi ? 'शत्रु अंक (Enemy Numbers):' : 'Enemy Numbers (Shatru):'}
                </span>
                <div className="flex flex-wrap gap-2">
                  {NUMBER_RELATIONS[analysis.moolank].enemies.map((n) => (
                    <span key={n} className="w-8 h-8 rounded-lg bg-rose-600 text-white font-bold flex items-center justify-center text-sm shadow-sm">
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 5: PERSONAL YEAR CYCLE FORECAST */}
      {activeSection === 'forecast' && (
        <div className="space-y-6">
          <div className="bg-astro-bg p-6 sm:p-8 rounded-2xl border border-astro-border shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-astro-border pb-4">
              <div>
                <h3 className="text-xl font-bold font-serif text-astro-text">
                  {isHi ? `वर्षफल अंक चक्र - वर्ष ${new Date().getFullYear()}` : `Personal Year Cycle Forecast - ${new Date().getFullYear()}`}
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi ? 'वर्तमान वर्ष की तरंग और मासिक प्राथमिकताओं का विस्तृत काल-चक्र' : 'Annual and monthly energetic vibrations guiding career, finances, and relationships.'}
                </p>
              </div>
              <div className="flex items-center gap-3 bg-astro-card px-4 py-2 rounded-xl border border-astro-border">
                <span className="text-xs font-bold text-astro-secondary-text">{isHi ? 'सक्रिय वर्ष अंक:' : 'Active Year Number:'}</span>
                <span className="text-2xl font-extrabold font-serif text-astro-primary">
                  {analysis.personalYear}
                </span>
              </div>
            </div>

            {/* Current Active Year Hero Card */}
            <div className="bg-gradient-to-br from-astro-primary/5 to-amber-500/10 p-6 rounded-2xl border border-astro-primary/20 space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <h4 className="text-sm font-bold text-astro-primary uppercase tracking-wider">
                  {isHi ? `व्यक्तिगत वर्ष ${analysis.personalYear} का वार्षिक फल` : `Personal Year ${analysis.personalYear} Guidance`}
                </h4>
              </div>
              <p className="text-sm text-astro-text leading-relaxed font-medium">
                {isHi ? analysis.personalYearForecastHi : analysis.personalYearForecast}
              </p>
            </div>

            {/* 9 Year Cycle Timeline */}
            <div>
              <h4 className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider mb-3">
                {isHi ? '9 वर्षीय काल-चक्र विहंगम:' : '9-Year Vibrational Epicycle Timeline:'}
              </h4>
              <div className="grid grid-cols-3 sm:grid-cols-9 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((yr) => {
                  const isCurrent = yr === analysis.personalYear;
                  return (
                    <div
                      key={yr}
                      className={`p-3 rounded-xl border text-center transition-all ${
                        isCurrent
                          ? 'bg-astro-primary text-white border-astro-primary shadow-md scale-105'
                          : 'bg-astro-card text-astro-text border-astro-border'
                      }`}
                    >
                      <span className="text-lg font-extrabold font-serif block">{yr}</span>
                      <span className="text-[9px] font-semibold uppercase tracking-wider block mt-0.5">
                        {yr === 1 ? 'Seed' : yr === 5 ? 'Shift' : yr === 8 ? 'Harvest' : yr === 9 ? 'Reset' : 'Phase'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 6: VEDIC REMEDIES & AUSPICIOUS FACTORS */}
      {activeSection === 'remedies' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Gemstone Recommendation */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-[#52635F]">
                <Award className="w-5 h-5" />
                <h4 className="text-sm font-bold font-serif">{isHi ? 'शुभ रत्न (Gemstone)' : 'Favorable Gemstone'}</h4>
              </div>
              <p className="text-base font-bold text-astro-primary">
                {isHi ? analysis.recommendedGemstoneHi : analysis.recommendedGemstone}
              </p>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? 'मूलांक स्वामी ग्रह को बलवान करने हेतु दाहिने हाथ की उचित उंगली में धारण करें।' : 'Wear on the designated finger to amplify your ruling planetary vitality.'}
              </p>
            </div>

            {/* Rudraksha Recommendation */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-[#52635F]">
                <Sparkles className="w-5 h-5" />
                <h4 className="text-sm font-bold font-serif">{isHi ? 'रुद्राक्ष कवच (Rudraksha)' : 'Prescribed Rudraksha'}</h4>
              </div>
              <p className="text-base font-bold text-astro-primary">
                {analysis.recommendedRudraksha}
              </p>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? 'प्रातः स्नान के उपरांत ॐ नमः शिवाय का जप करते हुए लाल या पीले धागे में धारण करें।' : 'Empowers the aura and balances subtle bio-energies against stress.'}
              </p>
            </div>

            {/* Yantra & Mantra */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-astro-primary">
                <Shield className="w-5 h-5" />
                <h4 className="text-sm font-bold font-serif">{isHi ? 'यंत्र एवं बीज मंत्र' : 'Yantra & Mantra'}</h4>
              </div>
              <p className="text-xs font-bold text-astro-text">
                {analysis.recommendedYantra}
              </p>
              <div className="bg-astro-card p-2.5 rounded-lg border border-astro-border text-xs font-mono text-astro-primary font-bold">
                {analysis.recommendedMantra}
              </div>
            </div>

            {/* Lucky Dates */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm space-y-2">
              <div className="flex items-center gap-2 text-astro-primary">
                <Calendar className="w-4 h-4" />
                <h4 className="text-xs font-bold uppercase tracking-wider">{isHi ? 'माह की शुभ तिथियां' : 'Auspicious Monthly Dates'}</h4>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {analysis.luckyDates.map((d) => (
                  <span key={d} className="px-2.5 py-1 bg-astro-primary text-white text-xs font-bold rounded-lg">
                    {d}
                  </span>
                ))}
              </div>
            </div>

            {/* Color Harmonics: Lucky, Neutral & Avoid Colors */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-astro-border pb-2">
                <div className="flex items-center gap-2 text-astro-primary">
                  <Palette className="w-4 h-4 text-rose-600" />
                  <h4 className="text-xs font-bold uppercase tracking-wider">
                    {isHi ? 'रंग सामंजस्य (शुभ, तटस्थ व वर्जित रंग)' : 'Color Harmonics (Lucky, Neutral & Avoid)'}
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('astro_open_lucky_modal'))}
                  className="text-[10px] font-bold text-astro-accent underline cursor-pointer"
                >
                  {isHi ? 'विस्तृत मार्गदर्शिका' : 'Full Guide'}
                </button>
              </div>

              {/* 1. Lucky Colors */}
              <div className="space-y-1.5">
                <span className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  {isHi ? '१. शुभ रंग (Lucky & Auspicious):' : '1. Auspicious Colors:'}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {colorTaxonomy.lucky.items.map((col, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs font-semibold rounded-md text-emerald-900 dark:text-emerald-200"
                    >
                      <span className="w-2.5 h-2.5 rounded-full border border-black/20" style={{ backgroundColor: col.hex }} />
                      {col.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* 2. Neutral Colors */}
              <div className="space-y-1.5">
                <span className="text-[11px] font-bold text-sky-800 dark:text-sky-300 flex items-center gap-1">
                  <MinusCircle className="w-3.5 h-3.5 text-sky-600" />
                  {isHi ? '२. तटस्थ रंग (Neutral & Routine Balance):' : '2. Neutral Colors:'}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {colorTaxonomy.neutral.items.map((col, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800 text-xs font-semibold rounded-md text-sky-900 dark:text-sky-200"
                    >
                      <span className="w-2.5 h-2.5 rounded-full border border-black/20" style={{ backgroundColor: col.hex }} />
                      {col.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* 3. Avoid Colors */}
              <div className="space-y-1.5">
                <span className="text-[11px] font-bold text-rose-800 dark:text-rose-300 flex items-center gap-1">
                  <Ban className="w-3.5 h-3.5 text-rose-600" />
                  {isHi ? '३. वर्जित / टालने योग्य रंग (Colors to Avoid):' : '3. Colors to Avoid:'}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {colorTaxonomy.avoid.items.map((col, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-xs font-semibold rounded-md text-rose-900 dark:text-rose-200"
                    >
                      <span className="w-2.5 h-2.5 rounded-full border border-rose-500" style={{ backgroundColor: col.hex }} />
                      {col.name}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-astro-secondary-text pt-0.5">
                  <span className="font-semibold text-rose-700 dark:text-rose-400">{isHi ? 'कारण: ' : 'Reason: '}</span>
                  {colorTaxonomy.avoid.rationale}
                </p>
              </div>
            </div>

            {/* Favorable Directions */}
            <div className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm space-y-2">
              <div className="flex items-center gap-2 text-astro-primary">
                <Compass className="w-4 h-4" />
                <h4 className="text-xs font-bold uppercase tracking-wider">{isHi ? 'अनुकूल दिशाएं (Directions)' : 'Vastu Directions'}</h4>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(isHi ? analysis.favorableDirectionsHi : analysis.favorableDirections).map((dir, i) => (
                  <span key={i} className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-xs font-semibold rounded-md text-emerald-800">
                    {dir}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 4: NAME & BRAND COMPARISON ENGINE */}
      {activeSection === 'brand-compare' && (
        <NameBrandComparisonEngine kundali={kundali} lang={lang} />
      )}
    </div>
  );
};
